/**
 * @file HybridModelSessionContext.ts
 *
 * Manages a real-time speech recognition and translation session that uses the dual-model
 * ("hybrid") strategy. Selected when `modelType` is `'hybridModel'` via `SessionResolver`.
 *
 * Responsibilities:
 * - Configures the Azure `SpeechTranslationConfig` with `OutputFormat.Detailed` to receive
 *   both quick (partial) and stable (final) recognition results from the same recognizer.
 * - Instantiates either a `SpeechRecognizer` (transcription-only) or a `TranslationRecognizer`
 *   (transcription + translation) based on whether output languages are configured.
 * - Creates and owns one `HybridModelText2SpeechProcessor` per output language. Unlike the
 *   standard `Text2SpeechProcessor`, this processor accepts an `isStable` flag per enqueue
 *   call, enabling it to play quick partial audio immediately while replacing it with higher-
 *   quality stable audio once the recognizer finalizes the utterance.
 * - Forwards partial results via `enqueueText(offset, text, false)` on `recognizing` events,
 *   and stable results via `enqueueText(offset, text, true)` on `recognized` events.
 * - Propagates recognition callbacks (`onPartialRecognitionReady`, `onRecognitionReady`,
 *   `onPartialTranslationReady`, `onTranslationReady`, `onSessionCanceled`) identically to
 *   `SessionContext` so the two implementations are interchangeable at the call site.
 * - Exposes `setSpeechToken()`, `changeInputDevice()`, `start()`, `stop()`, and `setOutputVoice()`
 *   with the same interface contract as `SessionContext`.
 * - Follows the same `READY` → `RUNNING` → `STOPPED` state machine.
 *
 * Key behavioral difference from `SessionContext`:
 *   `SessionContext` feeds only stable (final) text to its TTS processors.
 *   `HybridModelSessionContext` feeds both partial and stable text to `HybridModelText2SpeechProcessor`,
 *   which manages offset-based deduplication internally to avoid re-speaking already-played audio.
 *
 * Pipeline position:
 *   AudioInputHandler → TranslationRecognizer (this file)
 *     → HybridModelText2SpeechProcessor → AudioPlayer
 *
 * Instantiated and managed exclusively through `SessionResolver.createSessionContext()`.
 */
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import {PropertyId, SpeechRecognizer, TranslationRecognizer} from 'microsoft-cognitiveservices-speech-sdk';
import {Input, Language, Output, TranslationResult, TranslationsBody} from './Configuration';
import {HybridModelText2SpeechProcessor} from '../core/HybridModelText2SpeechProcessor';
import {CancellationReason} from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports';
import {UUID} from 'crypto';
import MediaDevicesUtils from "../utils/MediaDevicesUtils";
import MediaDeviceInfoWithState from "../utils/MediaDeviceInfoPlus";

const azureRegion = 'eastus';
const LOG_PREFIX = '[HybridSessionContext]';

export default class HybridModelSessionContext {

    private sessionId: UUID;
    private selectedOutputs: Output[];
    private selectedInput: Input;
    private inputLangKey: string;
    private azureSpeechAPIToken: string;
    private onPartialRecognitionReady: (recognizedText: string) => void;
    private onRecognitionReady: (recognizedText: string) => void;
    private onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void;
    private onTranslationReady: (translationBody: TranslationsBody) => void;
    private onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason, errorMsg: string) => void;

    private speechRecognitionConfig: sdk.SpeechTranslationConfig | undefined;

    private speechRecognizer: SpeechRecognizer | undefined;
    private translationRecognizer: TranslationRecognizer | undefined;

    private readonly speech2TextProcessorByLanguage: Map<string, HybridModelText2SpeechProcessor>;
    private state: 'READY' | 'RUNNING' | 'STOPPED';

    public setSpeechToken(newToken: string) {
        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.setSpeechToken(newToken);
        });

        if (this.speechRecognizer) {
            this.speechRecognizer.authorizationToken = newToken;
        }

        if (this.translationRecognizer) {
            this.translationRecognizer.authorizationToken = newToken;
        }

        console.log(`${LOG_PREFIX} Azure token refreshed — speechRecognizer: ${!!this.speechRecognizer}, translationRecognizer: ${!!this.translationRecognizer}, ttsProcessors: ${this.speech2TextProcessorByLanguage?.size ?? 0}`);
    }

    constructor(azureSpeechAPIToken: string, sessionId: UUID, selectedInput: Input, selectedOutputs: Output[],
                onPartialRecognitionReady: (recognizedText: string) => void,
                onRecognitionReady: (recognizedText: string) => void,
                onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void,
                onTranslationReady: (translationsBody: TranslationsBody) => void,
                onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason, errorMsg: string) => void) {

        this.sessionId = sessionId;
        this.selectedInput = selectedInput;
        this.inputLangKey = selectedInput.language.recognitionKey.split('-')[0];
        this.selectedOutputs = selectedOutputs;
        this.azureSpeechAPIToken = azureSpeechAPIToken;
        this.onPartialRecognitionReady = onPartialRecognitionReady;
        this.onRecognitionReady = onRecognitionReady;
        this.onPartialTranslationReady = onPartialTranslationReady;
        this.onTranslationReady = onTranslationReady;
        this.onSessionCanceled = onSessionCanceled;

        console.log(`${LOG_PREFIX} Constructor — input: ${this.selectedInput.language.recognitionKey}, outputs: ${this.selectedOutputs.map(o => o.language.speakKey).join(', ')}`);

        this.speech2TextProcessorByLanguage = new Map<string, HybridModelText2SpeechProcessor>();
        this.selectedOutputs.forEach(output => {
            this.speech2TextProcessorByLanguage.set(output.language.translationKey, this.startText2SpeechProcessor(output, azureSpeechAPIToken));
            console.log(`${LOG_PREFIX} Text2Speech processor started — lang: ${output.language.speakKey}`);
        });

        this.state = 'READY';
        console.log(`${LOG_PREFIX} All processors ready. Session context is ready to start recognition.`);
    }

    public start() {
        return MediaDevicesUtils.instance().getInputDeviceStream(this.selectedInput.device.deviceId, this.selectedInput.gain)
            .then(inputMediaStream => {
                if (!inputMediaStream) {
                    throw new Error('Cannot start session. Cannot get audio from microphone. Check the microphone is properly connected and able to provide audio input. If the microphone is connected, please check you have granted permissions to it');
                }

                this.setupRecognition(inputMediaStream);

                try {
                    this.speech2TextProcessorByLanguage?.forEach(processor => {
                        processor.start()
                    });
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error starting processors: `, e);
                    this.speech2TextProcessorByLanguage?.forEach(processor => {
                        try {
                            processor.stop()
                        } catch (ignore) {}
                    });
                    try {
                        this.speechRecognizer?.stopContinuousRecognitionAsync();
                        this.translationRecognizer?.stopContinuousRecognitionAsync();
                    } catch (ignore) {}
                }

                this.state = 'RUNNING';
                console.log(`${LOG_PREFIX} Started.`);
            })
            .catch(err => {
                const message = 'Cannot start session. The selected microphone is not connected or is not active. ' +
                    'Please check the microphone is properly connected and able to provide audio input.';

                console.error(message, err);
                throw new Error(message);
            })
    }

    private parseTranslations(
        translationKeys: string[],
        getText: (key: string) => string,
        originalText: string
    ): Map<Language, TranslationResult> {
        const parsed = new Map<Language, TranslationResult>();
        translationKeys.forEach(translationKey => {
            const lang = {id: translationKey} as Language;
            const translationResult = {
                originalText: originalText || '',
                translatedText: getText(translationKey),
                status: '',
                language: lang
            } as TranslationResult;
            parsed.set(lang, translationResult);
        });
        return parsed;
    }

    private setupRecognition(inputMediaStream: MediaStream) {
        const audioConfig = sdk.AudioConfig.fromStreamInput(inputMediaStream);

        this.speechRecognitionConfig = sdk.SpeechTranslationConfig.fromAuthorizationToken(this.azureSpeechAPIToken, azureRegion);
        if (!this.speechRecognitionConfig) {
            throw new Error('No speech recognition configuration found.');
        }
        this.speechRecognitionConfig.setProperty(PropertyId.Speech_SegmentationSilenceTimeoutMs, '400');
        this.speechRecognitionConfig.outputFormat = sdk.OutputFormat.Detailed;
        this.speechRecognitionConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey;

        this.selectedOutputs.forEach(output => {
            console.log(`${LOG_PREFIX} Adding translation target language: `, output);
            // @ts-ignore
            this.speechRecognitionConfig.addTargetLanguage(output.language.translationKey);
        });

        if (this.selectedOutputs.length === 0) {
            // Transcription only — use a plain speech recognizer
            this.speechRecognizer = new sdk.SpeechRecognizer(this.speechRecognitionConfig, audioConfig);

            this.speechRecognizer.recognizing = (sender, event) => {
                this.onPartialRecognitionReady(event.result.text);
            }

            this.speechRecognizer.recognized = (sender, event) => {
                this.onRecognitionReady(event.result.text);
            }

            this.speechRecognizer.canceled = (sender, event) => {
                this.onSessionCanceled(this.sessionId, event.sessionId, event.reason, event.errorDetails);
            }

        } else {
            // Transcription + translation — use a translation recognizer
            this.translationRecognizer = new sdk.TranslationRecognizer(this.speechRecognitionConfig, audioConfig);

            this.translationRecognizer.recognizing = (sender, event) => {
                console.log(`${LOG_PREFIX} translationRecognizer.recognizing: `, event);

                if (event.result.reason === sdk.ResultReason.NoMatch) {
                    console.log(`${LOG_PREFIX} Skipping event — reason: `, event.result.reason, event);
                    return;
                }

                const partialText = event.result.text;
                this.onPartialRecognitionReady(partialText);

                const parsedTranslations = this.parseTranslations(
                    event.result.translations.languages,
                    key => event.result.translations.get(key),
                    partialText
                );

                parsedTranslations.forEach((translationResult, lang) => {
                    console.log(`${LOG_PREFIX} Sending PARTIAL to speech2TextProcessorByLanguage — lang: ${lang.id}`);
                    this.speech2TextProcessorByLanguage?.get(lang.id)?.enqueueText(event.result.offset + '', translationResult.translatedText, false);
                });

                this.onPartialTranslationReady(parsedTranslations);
            }

            this.translationRecognizer.recognized = (sender, event) => {
                console.log(`${LOG_PREFIX} translationRecognizer.recognized: `, event);

                if (event.result.reason === sdk.ResultReason.NoMatch) {
                    console.log(`${LOG_PREFIX} Skipping event — reason: `, event.result.reason, event);
                    return;
                }

                const recognizedText = event.result.text;
                this.onRecognitionReady(recognizedText);

                const parsedTranslations = this.parseTranslations(
                    event.result.translations.languages,
                    key => event.result.translations.get(key),
                    recognizedText
                );

                const translations: TranslationsBody = {inputs: {}, outputs: {}};
                parsedTranslations.forEach((translationResult, lang) => {
                    translations.outputs[lang.id] = translationResult.translatedText;
                    if (!translations.inputs[this.inputLangKey])
                        translations.inputs[this.inputLangKey] = recognizedText;

                    console.log(`${LOG_PREFIX} Sending STABLE to speech2TextProcessorByLanguage — lang: ${lang.id}`);
                    this.speech2TextProcessorByLanguage?.get(lang.id)?.enqueueText(event.result.offset + '', translationResult.translatedText, true);
                });

                this.onTranslationReady(translations);
            }

            this.translationRecognizer.sessionStarted = (sender, event) => {
                console.log(`${LOG_PREFIX} TranslationRecognizer started.`, event);
            }
            this.translationRecognizer.sessionStopped = (sender, event) => {
                console.log(`${LOG_PREFIX} TranslationRecognizer stopped.`, event);
            }
            this.translationRecognizer.canceled = (sender, event) => {
                console.error(`${LOG_PREFIX} TranslationRecognizer canceled.`, event);
                this.onSessionCanceled(this.sessionId, event.sessionId, event.reason, event.errorDetails);
            }
        }
    }

    public changeInputDevice(selectedInput: MediaDeviceInfo) {
        if (this.state !== 'RUNNING')
            return;

        if (!(selectedInput as MediaDeviceInfoWithState).isActive) {
            throw new Error('Selected microphone is disconnected or inactive. Please check the microphone is properly connected and able to provide audio input.');
        }

        if (this.selectedInput?.device?.deviceId) {
            MediaDevicesUtils.instance().freeAudioHandler(this.selectedInput.device.deviceId);
        }

        this.selectedInput = {
            ...this.selectedInput,
            device: selectedInput
        };

        MediaDevicesUtils.instance().getInputDeviceStream(this.selectedInput.device.deviceId, this.selectedInput.gain)
            .then(inputMediaStream => {
                if (!inputMediaStream) {
                    throw new Error('Selected microphone is not producing audio. Please check the microphone is properly connected and able to provide audio input.');
                }

                // Stop any recognizer before creating a new one
                this.speechRecognizer?.stopContinuousRecognitionAsync();
                this.translationRecognizer?.stopContinuousRecognitionAsync();

                // Dispose current instances to release resources before reassigning
                try { this.speechRecognizer?.close(); } catch {}
                try { this.translationRecognizer?.close(); } catch {}

                // Create a new recognizer using the new MediaStream
                this.setupRecognition(inputMediaStream);

                try {
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error replacing microphone: `, e);
                    try {
                        this.speechRecognizer?.stopContinuousRecognitionAsync();
                        this.translationRecognizer?.stopContinuousRecognitionAsync();
                    } catch (ignore) {}
                }
                console.log(`${LOG_PREFIX} Input device changed — deviceId: ${this.selectedInput.device.deviceId}`);
            })
    }

    public stop() {
        console.log(`${LOG_PREFIX} Stopping...`);
        this.speechRecognizer?.stopContinuousRecognitionAsync();
        this.speechRecognizer?.close();
        this.translationRecognizer?.stopContinuousRecognitionAsync();
        this.translationRecognizer?.close();
        this.speech2TextProcessorByLanguage?.forEach((processor, index) => {
            console.log(`${LOG_PREFIX} Stopping Text2Speech processor — config: ${processor.getOutputConfig()}, key: ${index}`);
            processor.stop();
        });
        this.state = 'STOPPED';
        console.log(`${LOG_PREFIX} Stopped.`);
    }

    public getState() {
        return this.state;
    }

    private startText2SpeechProcessor = (outputConfig: Output, azureSpeechAPIToken: string): HybridModelText2SpeechProcessor => {
        return new HybridModelText2SpeechProcessor(outputConfig, azureSpeechAPIToken);
    }

    public getText2Speech(translationKey: string) {
        return this.speech2TextProcessorByLanguage?.get(translationKey);
    }

    public setOutputVoice(translationKey: string, voiceName: string): void {
        console.log('OUTPUT VOICE SET', translationKey, voiceName, this.speech2TextProcessorByLanguage?.get(translationKey))
        this.speech2TextProcessorByLanguage?.get(translationKey)?.setVoiceName(voiceName);
    }

    public setOutputDevice(
        translationKey: string,
        device: MediaDeviceInfo | null,
        gain?: number,
        force: boolean = false
    ): void {
        this.speech2TextProcessorByLanguage?.get(translationKey)?.setOutputDevice(device, gain, force);
    }


}
